import { EditorState } from 'draft-js';
import React from 'react';
import PropTypes from 'prop-types';
import withStyles from 'isomorphic-style-loader/withStyles';

import { intlShape, injectIntl, defineMessages } from 'react-intl';

import _ from 'lodash';

import {
  Field,
  reduxForm,
  // SubmissionError,
  propTypes as reduxFormPropTypes,
  SubmissionError,
} from 'redux-form';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import Snackbar from '@material-ui/core/Snackbar';

import * as authActions from '../../actions/auth';
import RichEditor, {
  emptyContent,
  emptyContentState,
} from '../RichEditor/RichEditor';

import {
  ModalCloseBtn,
  ModalSaveBtn,
} from '../statelessComponents/Buttons/ButtonsInModals';
import TextField from '../statelessComponents/FormFields/TextField';
import { buildQuery } from '../../helpers/index';

import s from './EditDiscussionModal.scss';
import validate from './editDiscussionValidation';
import formUnsavedWarning from '../../decorators/formUnsavedWarning';
import AsyncAutocomplete from '../AsyncAutocomplete/AsyncAutocomplete';
import CircularProgress from '../CircularProgress';

const messages = defineMessages({
  successSavedMsg: {
    id: 'EditDiscussionModal.successSavedMsg',
    defaultMessage: 'Entry has been posted',
    description: 'Success snackbar message displayed in edit discussion modal',
  },
  subjectLabel: {
    id: 'EditDiscussionModal.subjectLabel',
    defaultMessage: 'Subject',
    description: 'Subject field label on edit discussion modal',
  },
  textLabel: {
    id: 'EditDiscussionModal.textLabel',
    defaultMessage: 'Message',
    description: 'Message field label on edit discussion modal',
  },
  courseLabel: {
    id: 'EditDiscussionModal.courseLabel',
    defaultMessage: 'Course',
    description: 'Course field label on edit discussion modal',
  },
  lessonLabel: {
    id: 'EditDiscussionModal.lessonLabel',
    defaultMessage: 'Lesson',
    description: 'Lesson field label on edit discussion modal',
  },
  save: {
    id: 'EditDiscussionModal.save',
    defaultMessage: 'Post message',
  },
});

@injectIntl
@withStyles(s)
@connect(
  state => ({
    initialValues: state.editDiscussion.discussion,
    followedDiscussions: state.auth.followedDiscussions,
    lang: state.intl.locale,
  }),
  dispatch =>
    bindActionCreators(
      {
        saveFollowedDiscussions: authActions.saveFollowedDiscussions,
      },
      dispatch,
    ),
)
@reduxForm({
  form: 'editDiscussionForm',
  validate,
})
@formUnsavedWarning
export default class EditDiscussionModal extends React.Component {
  static propTypes = {
    ...reduxFormPropTypes,
    onClose: PropTypes.func.isRequired,
    onSave: PropTypes.func.isRequired,
    saveFollowedDiscussions: PropTypes.func.isRequired,
    followedDiscussions: PropTypes.arrayOf(PropTypes.string.isRequired)
      .isRequired,
    intl: intlShape.isRequired,
    courseId: PropTypes.string,
    autoFollow: PropTypes.bool,
    lang: PropTypes.string.isRequired,
    mainGroup: PropTypes.shape({
      _id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
    }),
  };

  static defaultProps = {
    courseId: '',
    autoFollow: false,
    mainGroup: null,
  };

  static contextTypes = {
    fetch: PropTypes.func,
    primaryAutocompleteStyles: PropTypes.shape({}),
  };

  state = {
    isSubmitted: false,
    searchCourse: '',
  };

  richEditor = null;

  componentWillUnmount() {
    this.props.reset();
    if (this.richEditor) {
      this.richEditor.onChange(
        EditorState.createWithContent(emptyContentState),
      );
    }
  }

  getCourses = value => {
    const { fetch } = this.context;
    const { lang } = this.props;

    const params = {
      page: 1,
      search: _.trim(value),
      perPage: 50,
      lang,
    };
    const query = buildQuery(params);

    return fetch(`/api/courses?${query}`, { method: 'GET' })
      .then(async res => {
        const resText = await res.text();
        const json = resText ? JSON.parse(resText) : [];
        if (res.status >= 400) {
          console.error(json);
          return [];
        }

        return json.map(item => ({
          _id: item._id,
          name: item.name,
        }));
      }, console.error)
      .catch(console.error);
  };

  submit = data => {
    const { courseId, mainGroup } = this.props;

    const text = _.assign({}, emptyContent, data.text);
    const processedData = {
      ..._.omit(data, ['createdBy']),
      text,
    };

    if (courseId) {
      processedData.courseId = courseId;
      processedData.isCourseDiscussion = true;
    } else {
      processedData.courseId = _.get(data, 'courseId._id');
    }

    if (mainGroup) {
      processedData.mainGroupId = mainGroup._id;
    }

    return this.create(processedData);
  };

  create = data =>
    this.context
      .fetch('/api/discussions', {
        method: 'POST',
        body: JSON.stringify(data),
      })
      .catch(err => {
        throw new SubmissionError(err && err.errors);
      })
      .then(res =>
        res.json().then(resJson => {
          if (!res.ok) {
            throw new SubmissionError(resJson);
          }
          return resJson;
        }),
      )
      .then(discussion => {
        if (!this.props.autoFollow) {
          return discussion;
        }

        const { saveFollowedDiscussions, followedDiscussions } = this.props;

        return this.context
          .fetch(`/api/auth/followed-discussions/${discussion._id}`, {
            method: 'PUT',
            headers: {
              'Content-Type': undefined,
              Accept: '*/*',
            },
          })
          .then(() =>
            saveFollowedDiscussions(
              followedDiscussions.concat([discussion._id]),
            ),
          )
          .then(() => discussion);
      })
      .then(discussion => {
        this.setState({
          isSubmitted: true,
        });
        const { onClose, onSave } = this.props;
        onSave(discussion._id);
        setTimeout(onClose, 2000);
      });

  handleCloseSnackbar = () => {
    this.setState({ isSubmitted: false });
  };

  changeCourseSearchField = searchCourse => {
    this.setState({ searchCourse });
  };

  render() {
    const {
      handleSubmit,
      submitting,
      loading,
      onClose,
      intl: { formatMessage },
      courseId,
    } = this.props;

    const { isSubmitted, searchCourse } = this.state;

    if (loading) {
      return (
        <div className={s.root}>
          <div className={s.container}>
            <div className={s.loadingWrapper}>
              <CircularProgress size={100} />
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className={s.root}>
        <form noValidate autoComplete="off" data-test="discussion-form">
          <Field
            name="name"
            type="text"
            label={formatMessage(messages.subjectLabel)}
            margin="normal"
            component={TextField}
            fullWidth
          />
          {!courseId && (
            <Field
              name="courseId"
              margin="normal"
              component={AsyncAutocomplete}
              changeSearchField={this.changeCourseSearchField}
              searchValue={searchCourse}
              loadOptions={this.getCourses}
              placeholder={formatMessage(messages.courseLabel)}
              getOptionLabel={option => option.name}
              getOptionValue={option => option._id}
              styles={this.context.primaryAutocompleteStyles}
              defaultOptions
              isClearable
            />
          )}
          <Field
            name="text"
            label={formatMessage(messages.textLabel)}
            component={RichEditor}
            wrapperClassName={s.textField}
            blockTypes={[{ label: 'Blockquote', style: 'blockquote' }]}
            ref={node => {
              this.richEditor = node && node.getRenderedComponent();
            }}
            forwardRef
          />
          <ModalSaveBtn
            data-test-id="3UsGSDom0AY08CFrao0rN"
            onClick={handleSubmit(this.submit)}
            mobileLandscapeRelative
            disabled={submitting}
            isLoaderOn={submitting}
            tooltip={formatMessage(messages.save)}
            data-test="submit-discussion-button"
          />
          <ModalCloseBtn
            data-test-id="dM5k3XZSGbhSK7I6ARttZ"
            fitMobileLandscapeFullWidth
            onClick={onClose}
          />
        </form>
        <Snackbar
          open={isSubmitted}
          onClose={this.handleCloseSnackbar}
          autoHideDuration={3000}
          message={formatMessage(messages.successSavedMsg)}
        />
      </div>
    );
  }
}
